// AddViewSupport48Component.jsx
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { GetVMdDept } from "../../share/api/MasterDataApi";

// === NEW: export libs ===
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

const thaiMonths = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);


// แปลง ArrayBuffer -> base64 (ไม่กินสแต็ก)
function base64FromArrayBuffer(buf) {
  const bytes = new Uint8Array(buf);
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  return btoa(binary);
}

let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;

  // โหลด TTF จากโฟลเดอร์ public
  const [regRes, boldRes] = await Promise.all([
    fetch("/fonts/Sarabun-Regular.ttf"),
    fetch("/fonts/Sarabun-Bold.ttf"),
  ]);

  const [regBuf, boldBuf] = await Promise.all([
    regRes.arrayBuffer(),
    boldRes.arrayBuffer(),
  ]);

  const regB64 = base64FromArrayBuffer(regBuf);
  const boldB64 = base64FromArrayBuffer(boldBuf);

  // ใส่ไฟล์ฟอนต์เข้า VFS แล้ว register ชื่อฟอนต์ "Sarabun"
  jsPDF.API.events.push(['addFonts', function () {
    this.addFileToVFS("Sarabun-Regular.ttf", regB64);
    this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
    this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
    this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
  }]);

  // trigger addFonts ครั้งแรก
  const probe = new jsPDF();
  thaiFontLoaded = true;
}


// ---------- helpers ----------
function formatMonthYearBE(date) {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
}
function fmtMoney(n) {
  const num = Number(n || 0);
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function toThaiDateLabel(isoOrStr) {
  if (!isoOrStr) return "-";
  if (typeof isoOrStr === "string" && /\s[ก-๙]/.test(isoOrStr)) return isoOrStr;
  const d = typeof isoOrStr === "string" ? parseISO(isoOrStr) : isoOrStr;
  const be = addYears(543, d);
  const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "dd")} ${monthName} ${format(be, "yyyy")}`;
}
const groupBy = (arr, key) => arr.reduce((acc, it) => {
  const k = (it[key] ?? "").toString();
  (acc[k] ||= []).push(it);
  return acc;
}, {});
const isUniform = (rows, field) =>
  rows.every(r => (r[field] ?? "") === (rows[0]?.[field] ?? ""));

// ตัด duplicate แถวที่ “ทุกฟิลด์เหมือนกัน” ภายในกลุ่มเดียวกัน
function dedupeRowsByAllFields(rows) {
  const keyOf = (r) =>
    [
      r.SupportNo, r.SupportName, r.Year, r.PassTest,
      r.LawOccupationName, r.TestOccupationName, r.PersonTotal,
      r.PaymentAmount, r.PaymentDate, r.DeptName
    ].map(v => (v ?? "").toString().trim()).join("|¦|");
  const seen = new Set();
  const out = [];
  for (const r of rows) {
    const k = keyOf(r);
    if (!seen.has(k)) { seen.add(k); out.push(r); }
  }
  return out;
}

export function OperationReport4Component() {
  // ---------------- state ----------------
  const [reportMonth, setReportMonth] = useState(""); // "YYYY-MM"
  const [reportMonthDate, setReportMonthDate] = useState(null);

  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const roleId = String(user?.roleId ?? user?.roldId ?? "");
  const userDeptId = String(user?.deptId ?? "all");

  const [deptsData, setDeptsData] = useState([]);     // raw จาก API
  const [deptOptions, setDeptOptions] = useState([]); // [{value,label}]
  const [deptIdFilter, setDeptIdFilter] = useState("all");

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]); // แถวที่ map ตาม service แล้ว

  // ------------- master: หน่วยงาน -------------
  useEffect(() => {
    GetVMdDept({ setDeptsData, setLoading });
  }, []);

  useEffect(() => {
    const mapped = (deptsData || []).map(d => {
      const value = String(d?.value ?? d?.deptId ?? d?.DeptId ?? d?.id ?? "");
      const label = String(d?.label ?? d?.deptName ?? d?.DeptName ?? d?.name ?? value);
      return { value, label };
    });

    if (roleId === "4") {
      const withAll = [{ value: "all", label: "ทุกหน่วยงาน" }, ...mapped];
      setDeptOptions(withAll);
      setDeptIdFilter(prev => prev ?? "all");
    } else if (roleId === "3") {
      const selfOpt = mapped.find(o => o.value === userDeptId)
        || { value: userDeptId, label: "หน่วยงานของฉัน" };
      setDeptOptions([selfOpt]);
      setDeptIdFilter(userDeptId);
    } else {
      setDeptOptions(mapped);
      setDeptIdFilter(prev => prev || (mapped.find(o => o.value === userDeptId)?.value ?? ""));
    }
  }, [deptsData, roleId, userDeptId]);

  // ------------- datepicker sync -------------
  useEffect(() => {
    if (!reportMonth) { setReportMonthDate(null); return; }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  // ------------- load data -------------
  const checkAndLoadMonth = async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("monthYear", reportMonth === "" ? "all" : reportMonth);
      formData.append("deptIdFilter", deptIdFilter || "all");

      const res = await fetch("/api/SupportOperation/listSupportOperation4", {
        method: "POST",
        headers: { token: user.token },
        body: formData,
      });
      const j = await res.json();

      // สมมุติ payload = j.dataenum (ตามหลังบ้าน)
      const raw = Array.isArray(j?.dataenum) ? j.dataenum : [];

      // map ชื่อฟิลด์ให้ตรง service
      const mapped = raw.map((x) => ({
        SupportNo: x.SupportNo ?? x.supportNo ?? "",
        SupportName: x.SupportName ?? x.supportName ?? "",
        Year: x.Year ?? x.year ?? "",
        PassTest: x.PassTest ?? x.passTest ?? x.Total ?? x.total ?? "",
        LawOccupationName: x.LawOccupationName ?? x.lawOccupationName ?? "",
        TestOccupationName: x.TestOccupationName ?? x.testOccupationName ?? "",
        PersonTotal: x.PersonTotal ?? x.personTotal ?? x.Amount ?? x.amount ?? "",
        PaymentAmount: x.PaymentAmount ?? x.paymentAmount ?? "",
        PaymentDate: x.PaymentDate ?? x.paymentDate ?? "",
        DeptName: x.DeptName ?? x.deptName ?? ""
      }));

      const deduped = dedupeRowsByAllFields(mapped);
      setItems(deduped);
    } catch (e) {
      Swal.fire("โหลดข้อมูลเดือนไม่สำเร็จ", "", "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // รวมยอดเงินช่วยเหลือใน tfoot (กันบวกซ้ำ)
  const totalPayment = Object.values(groupBy(items, "SupportNo")).reduce((sum, rows) => {
    if (isUniform(rows, "PaymentAmount")) {
      const v = rows[0].PaymentAmount ?? 0;
      const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : Number(v);
      return sum + (isNaN(n) ? 0 : n);
    }
    return sum + rows.reduce((s, r) => {
      const v = r.PaymentAmount ?? 0;
      const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : Number(v);
      return s + (isNaN(n) ? 0 : n);
    }, 0);
  }, 0);

  // ---------- NEW: EXPORT HELPERS ----------
  const header = [
    "ที่",
    "เลขที่คำขอ",
    "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
    "หน่วยงานที่อนุม้ติคำขอ",
    "ปี พ.ศ.",
    "จำนวนผู้ผ่านการทดสอบฯ",
    "สาขาอาชีพ",
    "สาขา",
    "จำนวน (คน)",
    "จำนวนเงินช่วยเหลือ (บาท)",
    "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน",
  ];

  const showDeptCol =
    String(user?.roldId ?? roleId) === "4" &&
    String(deptIdFilter || "all") === "all";
  // เตรียม rows แบบ “ตามที่เห็นบนจอ”: กลุ่ม SupportNo, ค่าที่ซ้ำทั้งกลุ่มจะแสดงแค่แถวแรก
  const buildDisplayRowsAndMergePlan = () => {
    const groups = Object.entries(groupBy(items, "SupportNo"));
    const aoa = [header.slice()];
    const merges = [];
    let runningIndex = 1;
    let currentRow = 1;

    groups.forEach(([sn, rows]) => {
      const span = rows.length;
      const uniformYear = isUniform(rows, "Year");
      const uniformPass = isUniform(rows, "PassTest");
      const uniformAmt = isUniform(rows, "PaymentAmount");
      const uniformDate = isUniform(rows, "PaymentDate");

      // map index ตามว่ามีคอลัมน์ DeptName ไหม
      const COL_IDX = showDeptCol
        ? {
          No: 0,
          SupportNo: 1,
          SupportName: 2,
          DeptName: 3,
          Year: 4,
          PassTest: 5,
          LawOcc: 6,
          TestOcc: 7,
          Person: 8,
          Amount: 9,
          PayDate: 10,
        }
        : {
          No: 0,
          SupportNo: 1,
          SupportName: 2,
          Year: 3,
          PassTest: 4,
          LawOcc: 5,
          TestOcc: 6,
          Person: 7,
          Amount: 8,
          PayDate: 9,
        };

      rows.forEach((r, idx) => {
        const row = new Array(header.length).fill("");

        // ลำดับ
        row[COL_IDX.No] = idx === 0 ? runningIndex : "";

        // เลขที่คำขอ/ชื่อผู้ขอ (merge ทั้งกลุ่ม)
        if (idx === 0) {
          row[COL_IDX.SupportNo] = r.SupportNo || "-";
          row[COL_IDX.SupportName] = r.SupportName || "-";
          if (span > 1) {
            merges.push({
              s: { r: currentRow, c: COL_IDX.SupportNo },
              e: { r: currentRow + span - 1, c: COL_IDX.SupportNo },
            });
            merges.push({
              s: { r: currentRow, c: COL_IDX.SupportName },
              e: { r: currentRow + span - 1, c: COL_IDX.SupportName },
            });
          }
        }

        // หน่วยงาน (merge ทั้งกลุ่มถ้ามีการแสดง)
        if (showDeptCol) {
          if (idx === 0) {
            row[COL_IDX.DeptName] = r.DeptName || "-";
            if (span > 1) {
              merges.push({
                s: { r: currentRow, c: COL_IDX.DeptName },
                e: { r: currentRow + span - 1, c: COL_IDX.DeptName },
              });
            }
          }
        }

        // ปี พ.ศ. (merge ถ้า uniform)
        row[COL_IDX.Year] = uniformYear ? (idx === 0 ? (r.Year || "-") : "") : (r.Year || "-");
        if (uniformYear && idx === 0 && span > 1) {
          merges.push({
            s: { r: currentRow, c: COL_IDX.Year },
            e: { r: currentRow + span - 1, c: COL_IDX.Year },
          });
        }

        // ผ่านทดสอบฯ (merge ถ้า uniform)
        row[COL_IDX.PassTest] = uniformPass ? (idx === 0 ? (r.PassTest || "-") : "") : (r.PassTest || "-");
        if (uniformPass && idx === 0 && span > 1) {
          merges.push({
            s: { r: currentRow, c: COL_IDX.PassTest },
            e: { r: currentRow + span - 1, c: COL_IDX.PassTest },
          });
        }

        // สาขาอาชีพ/สาขา/จำนวน(คน) แสดงทุกแถว
        row[COL_IDX.LawOcc] = r.LawOccupationName || "-";
        row[COL_IDX.TestOcc] = r.TestOccupationName || "-";
        row[COL_IDX.Person] = r.PersonTotal || "-";

        // จำนวนเงิน (merge ถ้า uniform)
        const amtStr = (typeof r.PaymentAmount === "string" ? r.PaymentAmount : fmtMoney(r.PaymentAmount)) || "-";
        row[COL_IDX.Amount] = uniformAmt ? (idx === 0 ? amtStr : "") : amtStr;
        if (uniformAmt && idx === 0 && span > 1) {
          merges.push({
            s: { r: currentRow, c: COL_IDX.Amount },
            e: { r: currentRow + span - 1, c: COL_IDX.Amount },
          });
        }

        // วันที่จ่าย (merge ถ้า uniform)
        const dateStr = toThaiDateLabel(r.PaymentDate);
        row[COL_IDX.PayDate] = uniformDate ? (idx === 0 ? dateStr : "") : dateStr;
        if (uniformDate && idx === 0 && span > 1) {
          merges.push({
            s: { r: currentRow, c: COL_IDX.PayDate },
            e: { r: currentRow + span - 1, c: COL_IDX.PayDate },
          });
        }

        aoa.push(row);
        currentRow += 1;
      });

      runningIndex += 1;
    });

    return { aoa, merges };
  };


  // ---------- NEW: EXPORT ACTIONS ----------
  const handleExportExcel = () => {
    if (!items.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }
    const { aoa, merges } = buildDisplayRowsAndMergePlan();

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;

    // ความกว้างคอลัมน์ (dynamic ตาม showDeptCol)
    const cols = showDeptCol
      ? [
        { wch: 4 },   // ที่
        { wch: 16 },  // เลขที่คำขอ
        { wch: 40 },  // ชื่อผู้ขอฯ
        { wch: 28 },  // หน่วยงาน (NEW)
        { wch: 8 },   // ปี
        { wch: 14 },  // ผู้ผ่าน
        { wch: 20 },  // สาขาอาชีพ
        { wch: 24 },  // สาขา
        { wch: 12 },  // จำนวน(คน)
        { wch: 20 },  // จำนวนเงิน
        { wch: 22 },  // วันที่จ่าย
      ]
      : [
        { wch: 4 },
        { wch: 16 },
        { wch: 40 },
        { wch: 8 },
        { wch: 14 },
        { wch: 20 },
        { wch: 24 },
        { wch: 12 },
        { wch: 20 },
        { wch: 22 },
      ];
    ws["!cols"] = cols;

    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ4");
    const filename = `รายงาน_ชอ4_${reportMonth || "ทั้งหมด"}_${deptIdFilter}.xlsx`;
    XLSX.writeFile(wb, filename);
  };


  const handleExportPDF = async () => {
    if (!items.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }
    await ensureThaiFontLoaded();

    const { aoa } = buildDisplayRowsAndMergePlan();
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

    doc.setFont("Sarabun", "normal");
    doc.setFontSize(14);

    const title = `รายงานผู้ดำเนินการทดสอบฯ ตามมาตรา 24 (${formatMonthYearBE(reportMonthDate) || "ทุกเดือน"})`;
    doc.text(title, 40, 40);

    const [pdfHeader, ...pdfBody] = aoa;

    // คำนวณ index คอลัมน์แบบเดียวกับ buildDisplayRowsAndMergePlan
    const COL_IDX = showDeptCol
      ? {
        No: 0,
        SupportNo: 1,
        SupportName: 2,
        DeptName: 3,
        Year: 4,
        PassTest: 5,
        LawOcc: 6,
        TestOcc: 7,
        Person: 8,
        Amount: 9,
        PayDate: 10,
      }
      : {
        No: 0,
        SupportNo: 1,
        SupportName: 2,
        Year: 3,
        PassTest: 4,
        LawOcc: 5,
        TestOcc: 6,
        Person: 7,
        Amount: 8,
        PayDate: 9,
      };

    const columnStyles = {
      [COL_IDX.SupportName]: { halign: "left" },
      [COL_IDX.LawOcc]: { halign: "left" },
      [COL_IDX.TestOcc]: { halign: "left" },
      [COL_IDX.Amount]: { halign: "right" },
    };
    if (showDeptCol) columnStyles[COL_IDX.DeptName] = { halign: "left" };

    doc.autoTable({
      startY: 60,
      head: [pdfHeader],
      body: pdfBody,
      styles: {
        font: "Sarabun",
        fontStyle: "normal",
        fontSize: 10,
        cellPadding: 4,
        halign: "center",
        overflow: "linebreak",
      },
      headStyles: { fillColor: [230, 230, 230], font: "Sarabun", fontStyle: "bold" },
      columnStyles,
      didDrawPage: function (data) {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFont("Sarabun", "normal");
        doc.setFontSize(9);
        doc.text(
          `หน้า ${data.pageNumber} / ${pageCount}`,
          data.settings.margin.left,
          doc.internal.pageSize.getHeight() - 10
        );
      },
    });

    const filename = `รายงาน_ชอ4_${reportMonth || "ทั้งหมด"}_${deptIdFilter}.pdf`;
    doc.save(filename);
  };



  // ------------- render -------------
  return (
    <Container className="py-3">
      <Row className="g-3 align-items-end">
        {/* หน่วยงาน */}
        <Col md={6}>
          <Label>หน่วยงานกรมพัฒนาฝีมือแรงงาน</Label>
          <Select
            options={deptOptions}
            value={deptOptions.find(o => o.value === (deptIdFilter || "all")) || null}
            onChange={(opt) => setDeptIdFilter(opt?.value ?? "all")}
            isDisabled={roleId === "3"}
            className="text-center input-color"
            styles={{
              control: (base) => ({ ...base, borderRadius: "20px", color: "#0A66C2" }),
              singleValue: (base) => ({ ...base, color: "#0A66C2" }),
            }}
            placeholder="เลือกหน่วยงาน"
            noOptionsMessage={() => "ไม่พบหน่วยงาน"}
          />
        </Col>

        {/* เดือน/ปี */}
        <Col md={6}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`);
                setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else {
                setReportMonth("");
                setReportMonthDate(null);
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({
              date, changeYear, changeMonth, decreaseMonth, increaseMonth,
              prevMonthButtonDisabled, nextMonthButtonDisabled,
            }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light"
                  onClick={decreaseMonth} disabled={prevMonthButtonDisabled} title="เดือนก่อนหน้า">‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select
                    className="form-select form-select-sm" style={{ width: 120 }}
                    value={thaiMonths[getMonth(date)]}
                    onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}
                  >
                    {thaiMonths.map((m) => (<option key={m} value={m}>{m}</option>))}
                  </select>
                  <select
                    className="form-select form-select-sm" style={{ width: 100 }}
                    value={getYear(date)}
                    onChange={({ target: { value } }) => changeYear(Number(value))}
                  >
                    {years.map((y) => (<option key={y} value={y}>{y + 543}</option>))}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light"
                  onClick={increaseMonth} disabled={nextMonthButtonDisabled} title="เดือนถัดไป">›</button>
              </div>
            )}
            customInput={
              <input
                className="form-control rounded-pill text-center input-color"
                readOnly
                value={formatMonthYearBE(reportMonthDate)}
                placeholder="เลือกเดือน/ปี (พ.ศ.)"
              />
            }
          />
        </Col>

        <Col md="auto" className="pt-4 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={checkAndLoadMonth}>
            ตรวจสอบ/โหลดรายงาน
          </button>

          {/* NEW: export buttons */}
          <button className="btn btn-outline-success" onClick={handleExportExcel}>
            ส่งออก Excel
          </button>
          <button className="btn btn-outline-danger" onClick={handleExportPDF}>
            ส่งออก PDF
          </button>
        </Col>
      </Row>

      {/* ตาราง */}
      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan="2" className="text-center align-middle">ที่</th>
              <th rowSpan="2" className="text-center align-middle">เลขที่คำขอ</th>
              <th rowSpan="2" className="text-center align-middle">ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน</th>
              {user.roldId == 4 && deptIdFilter == "all" ? (
                <th rowSpan="2" className="text-center align-middle">หน่วยงานที่อนุมัติคำขอ</th>
              ) : null}
              <th colSpan="5" className="text-center">ผู้ดำเนินการทดสอบฯ ตามมาตรา 24</th>
              <th rowSpan="2" className="text-center align-middle">จำนวนเงินช่วยเหลือ (บาท)</th>
              <th rowSpan="2" className="text-center align-middle">วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
            </tr>
            <tr>
              <th className="text-center">ปี พ.ศ.</th>
              <th className="text-center">จำนวนผู้ผ่านการทดสอบฯ</th>
              <th className="text-center">สาขาอาชีพ</th>
              <th className="text-center">สาขา</th>
              <th className="text-center">จำนวน (คน)</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={10} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {Object.entries(groupBy(items, "SupportNo")).map(([sn, rows], groupIdx) => {
              const span = rows.length;
              const uniformYear = isUniform(rows, "Year");
              const uniformPassTest = isUniform(rows, "PassTest");
              const uniformPaymentAmount = isUniform(rows, "PaymentAmount");
              const uniformPaymentDate = isUniform(rows, "PaymentDate");


              return rows.map((r, idx) => (
                <tr key={`${sn}-${idx}`}>
                  <td className="text-center">{idx === 0 ? groupIdx + 1 : ""}</td>

                  {idx === 0 && (
                    <td className="text-center" rowSpan={span}>
                      {r.SupportNo || "-"}
                    </td>
                  )}
                  {idx === 0 && (
                    <td className="text-start" rowSpan={span}>
                      {r.SupportName || "-"}
                    </td>
                  )}
                  {idx === 0 && user?.roldId == 4 && deptIdFilter == "all" && (
                    <td className="text-start" rowSpan={span}>
                      {r.DeptName || "-"}
                    </td>
                  )}

                  {uniformYear ? (
                    idx === 0 ? <td className="text-center" rowSpan={span}>{r.Year || "-"}</td> : null
                  ) : (
                    <td className="text-center">{r.Year || "-"}</td>
                  )}

                  {uniformPassTest ? (
                    idx === 0 ? <td className="text-center" rowSpan={span}>{r.PassTest || "-"}</td> : null
                  ) : (
                    <td className="text-center">{r.PassTest || "-"}</td>
                  )}

                  <td className="text-center">{r.LawOccupationName || "-"}</td>
                  <td className="text-center">{r.TestOccupationName || "-"}</td>
                  <td className="text-center">{r.PersonTotal || "-"}</td>

                  {uniformPaymentAmount ? (
                    idx === 0 ? (
                      <td className="text-end" rowSpan={span}>
                        {typeof r.PaymentAmount === "string" ? r.PaymentAmount : fmtMoney(r.PaymentAmount)}
                      </td>
                    ) : null
                  ) : (
                    <td className="text-end">
                      {typeof r.PaymentAmount === "string" ? r.PaymentAmount : fmtMoney(r.PaymentAmount)}
                    </td>
                  )}

                  {uniformPaymentDate ? (
                    idx === 0 ? (
                      <td className="text-center" rowSpan={span}>
                        {toThaiDateLabel(r.PaymentDate)}
                      </td>
                    ) : null
                  ) : (
                    <td className="text-center">{toThaiDateLabel(r.PaymentDate)}</td>
                  )}
                </tr>
              ));
            })}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={8} className="text-end fw-bold">รวมจำนวนเงินช่วยเหลือ</td>
                <td className="text-end fw-bold">{fmtMoney(totalPayment)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <div className="badge bg-secondary">กำลังโหลด...</div>
        </div>
      )}
    </Container>
  );
}
